/**
 * Tells a tester they are in a test workspace, on every signed-in page.
 *
 * Shown only when the backend marks the workspace as a test one (see
 * ./workspace); every other answer, and the wait for it, draws nothing, so an
 * ordinary customer's pages never move for it.
 *
 * Not dismissible: the line it carries -- these are real people, and only your
 * own test candidates can be invited -- matters on the page where someone is
 * about to try to invite one, not only on the first page they saw.
 *
 * A labelled region, not a live region. Screen-reader users can find it from
 * the landmarks list and meet it first in reading order, but it is not
 * announced again on every page the way role="alert" or role="status" would.
 *
 * Copy says "test" (test workspace, test candidate), never "test drive".
 */

import { memo, useId, useState } from 'react';
import { ExperimentOutlined } from '@ant-design/icons';

import { settledWorkspace, useWorkspace } from './workspace';
import styles from './index.module.css';

export const TEST_WORKSPACE_TITLE = 'Test workspace';

export const BY_NAME_LINE =
  'Matched candidates are real job seekers, shown by name. You can open and invite only your test candidates.';

export const ANONYMISED_LINE =
  'Matched candidates are real job seekers, shown anonymised. You can open and invite only your test candidates.';

export const TestWorkspaceBanner = memo(() => {
  const { testWorkspace, jobSeekersAnonymised } = useWorkspace();
  const titleId = useId();
  // Fades in only when the answer arrives after the first frame, which is
  // once per session. A page that already knows draws it in place.
  const [knownOnMount] = useState(() => settledWorkspace() !== undefined);

  if (!testWorkspace) return null;

  return (
    <section
      className={
        knownOnMount ? styles.banner : `${styles.banner} ${styles.arrived}`
      }
      aria-labelledby={titleId}
    >
      <ExperimentOutlined className={styles.icon} aria-hidden="true" />
      {/* A run-in title: one paragraph, so a narrow screen wraps the sentence
          straight after the title instead of spending a line on it. */}
      <p className={styles.body}>
        <strong id={titleId} className={styles.title}>
          {TEST_WORKSPACE_TITLE}
        </strong>{' '}
        {jobSeekersAnonymised ? ANONYMISED_LINE : BY_NAME_LINE}
      </p>
    </section>
  );
});

TestWorkspaceBanner.displayName = 'TestWorkspaceBanner';

export default TestWorkspaceBanner;
